import { Divider, Skeleton } from "@mui/material";
import { useTranslations } from "use-intl";

import { LazyImage } from "@/components/Image/LazyImage";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { boardIcons } from "@/features/hotels/components/hotels/BoardIcons";
import { HotelName } from "@/features/hotels/components/hotels/HotelName";
import { HotelStars } from "@/features/hotels/components/hotels/HotelStars";
import type { Hotel } from "@/features/hotels/types/types";
import { includeBnplFee, calculateMonthlyInstallment } from "@/utils/bnpl-fee";
import { formatCurrency } from "@/utils/currency";
import { Feature, isFeatureEnabled } from "@/utils/featureFlags";

export const HotelCard = ({
  hotel,
  onClick,
}: {
  hotel: Hotel;
  onClick?: () => void;
}) => {
  const t = useTranslations();
  const { maxAvailableInstallments, isEligible } = usePaymentContext();
  const isBnplFeatureEnabled =
    isFeatureEnabled(Feature.Bnpl) && Boolean(isEligible);

  return (
    <button
      type="button"
      className="flex w-full flex-col gap-4 rounded-3xl bg-white p-1 text-start"
      onClick={onClick}
    >
      <div className="flex min-h-[186px] w-full justify-between">
        <div className="flex w-[130px] shrink-0 items-center overflow-clip rounded-s-2xl bg-gray-100 text-center text-xs text-text-tertiary">
          {hotel.mainImageUrl ? (
            <LazyImage
              className="size-full object-cover contain-size"
              sentryElementClassName="self-start"
              src={hotel.mainImageUrl}
              alt={hotel.name}
            />
          ) : (
            <span className="grow">{t("noImage")}</span>
          )}
        </div>

        <div className="flex w-full flex-col p-3">
          <span className="mb-1 line-clamp-2 text-base/snug font-medium text-text-title">
            <HotelName name={hotel.name} />
          </span>

          {!!hotel.stars && <HotelStars stars={hotel.stars} />}

          <div className="mt-2 mb-auto flex flex-col gap-2 text-text-primary">
            {hotel.specialDeal && (
              <div className="flex items-center">
                <span className="rounded-2xl bg-[#FBE6D0] px-3 py-1 text-xs">
                  {t("hotels.card.specialDeal")}
                </span>
              </div>
            )}
            {hotel.recommended && (
              <div className="flex items-center">
                <span className="rounded-2xl bg-surface-accent-strong px-3 py-1 text-xs text-text-inverted">
                  {t("hotels.card.recommended")}
                </span>
              </div>
            )}
            {hotel.boardBasis?.map((bb) => (
              <div key={bb} className="flex items-center">
                <div className="flex items-center gap-2 text-sm">
                  {boardIcons[bb]}
                  {t(`hotels.boardBasis.${bb}`)}
                </div>
              </div>
            ))}
          </div>

          {hotel.minPrice ? (
            <div className="mt-4 flex flex-col justify-start">
              <div className="text-start">
                <div className="text-sm text-text-tertiary">
                  {t("payment.standard")}
                </div>
                <div className="leading-tight">
                  <span className="text-base font-bold text-text-primary">
                    {formatCurrency(
                      hotel.minPrice.value,
                      hotel.minPrice.currency,
                    )}
                  </span>
                </div>
              </div>

              {isBnplFeatureEnabled ? (
                <>
                  <Divider
                    sx={{
                      borderColor: "var(--color-stroke-neutral-softer)",
                      my: "calc(var(--spacing)*2)",
                    }}
                  />
                  <div className="text-start">
                    <div className="text-sm text-text-tertiary">
                      {t("payment.payLater")}
                    </div>
                    <div className="leading-tight">
                      <span className="text-normal text-xs text-text-tertiary">
                        {t("orFrom")}
                      </span>{" "}
                      <span className="text-base font-bold text-text-primary">
                        {formatCurrency(
                          calculateMonthlyInstallment(
                            includeBnplFee(hotel.minPrice.value),
                            maxAvailableInstallments,
                          ),
                          hotel.minPrice.currency,
                        )}
                      </span>
                      <span className="text-sm text-text-tertiary">
                        {" "}
                        {t("payment.perMonth")}
                      </span>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </button>
  );
};

export const HotelCardSkeleton = () => {
  return (
    <div className="flex w-full flex-col gap-4 rounded-3xl bg-white p-1 text-start">
      <div className="flex min-h-[186px] w-full justify-between">
        <div className="flex w-[130px] shrink-0 items-center overflow-clip rounded-s-2xl">
          <Skeleton width="100%" height="100%" variant="rectangular" />
        </div>

        <div className="flex w-full flex-col gap-4 p-3">
          <Skeleton width="100%" height={68} />

          <Skeleton width={55} height={24} />

          <Skeleton width={110} height={24} />

          <Divider sx={{ borderColor: "var(--color-stroke-neutral-softer)" }} />

          <Skeleton width={110} height={24} />
        </div>
      </div>
    </div>
  );
};
